
import React, { useMemo, useState } from 'react';
import { Clock, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';
import { Button } from '@/components/ui/button';
import { useSwipeNavigation } from '@/hooks/useSwipeGestures';
import { cn } from '@/lib/utils';
import { ScheduleViewProps } from '../types/scheduleTypes';
import { Class, ClassSchedule } from '@/types/classTypes';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const CalendarScheduleView: React.FC<ScheduleViewProps> = ({
  schedules,
  classes,
  onScheduleUpdated,
  onScheduleDeleted
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Handle window resize for mobile detection
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const navigateToPreviousDay = React.useCallback(() => {
    setSelectedDate(prev => subDays(prev, 1));
  }, []);
  
  const navigateToNextDay = React.useCallback(() => {
    setSelectedDate(prev => addDays(prev, 1));
  }, []);
  
  const goToToday = React.useCallback(() => {
    setSelectedDate(new Date());
  }, []);
  
  // Setup swipe gestures for mobile navigation
  const swipeRef = useSwipeNavigation(navigateToPreviousDay, navigateToNextDay);
  // Helper function to find a class by ID
  const findClass = (classId: string) => {
    return classes.find(c => c.id === classId);
  };

  // Get unique class levels for color coding
  const classLevels = useMemo(() => {
    const levels = classes
      .map(cls => cls.level || 'No Level')
      .filter((value, index, self) => self.indexOf(value) === index);
    return levels;
  }, [classes]);

  // Assign colors to different class levels
  const getLevelColor = (level?: string) => {
    if (!level) return 'bg-gray-100 border-gray-200 text-gray-700';
    
    const index = classLevels.indexOf(level);
    const colors = [
      'bg-blue-100 border-blue-200 text-blue-700',
      'bg-green-100 border-green-200 text-green-700',
      'bg-purple-100 border-purple-200 text-purple-700',
      'bg-amber-100 border-amber-200 text-amber-700',
      'bg-rose-100 border-rose-200 text-rose-700',
      'bg-teal-100 border-teal-200 text-teal-700',
      'bg-indigo-100 border-indigo-200 text-indigo-700',
      'bg-emerald-100 border-emerald-200 text-emerald-700',
    ];
    
    return colors[index % colors.length];
  };

  // Days of week for our calendar
  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // Time slots for our calendar (assuming classes run from 8am to 8pm)
  const TIME_SLOTS = Array.from({ length: 13 }, (_, i) => {
    const hour = i + 8;
    return `${hour > 12 ? hour - 12 : hour}:00 ${hour >= 12 ? 'PM' : 'AM'}`;
  });

  // Organize schedules by day and time for the calendar
  const schedulesByDayAndTime = useMemo(() => {
    const result: Record<string, Record<string, (ClassSchedule & { classData: Class | undefined })[]>> = {};
    
    // Initialize empty arrays for each day and time slot
    DAYS.forEach(day => {
      result[day] = {};
      TIME_SLOTS.forEach(time => {
        result[day][time] = [];
      });
    });
    
    // Place schedules in the appropriate day and time slots
    schedules.forEach(schedule => {
      if (schedule.weekday === undefined || !schedule.start_time) return;
      
      const day = DAYS[schedule.weekday];
      const startHour = parseInt(schedule.start_time.split(':')[0]);
      const slotIndex = startHour - 8; // Adjust based on our 8am start
      
      if (slotIndex >= 0 && slotIndex < TIME_SLOTS.length) {
        const timeSlot = TIME_SLOTS[slotIndex];
        const classData = findClass(schedule.class_id);
        
        if (result[day] && result[day][timeSlot]) {
          result[day][timeSlot].push({ ...schedule, classData });
        }
      }
    });
    
    return result;
  }, [schedules, classes]);

  // Convert time from "HH:MM:SS" to "H:MM AM/PM"
  const formatTime = (time?: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${hour > 12 ? hour - 12 : hour}:${minutes} ${ampm}`;
  };

  return (
    <TooltipProvider>
      <div className="space-y-4 sm:space-y-6" ref={swipeRef}>
        {/* Mobile Day Navigation (only on mobile) */}
        {isMobile && (
          <div className="flex items-center justify-between bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-gray-200/60">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={navigateToPreviousDay}
                  variant="outline"
                  size="sm"
                  className="min-h-[44px] min-w-[44px] touch-manipulation"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Previous day (swipe right)</p>
              </TooltipContent>
            </Tooltip>
            
            <div className="text-center">
              <h2 className="text-lg font-bold text-gray-900">
                {format(selectedDate, 'EEEE, MMM d')}
              </h2>
              <p className="text-sm text-gray-600">
                {format(selectedDate, 'yyyy')}
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={goToToday}
                    variant="outline"
                    size="sm"
                    className="min-h-[44px] touch-manipulation"
                  >
                    <Calendar className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Go to today</p>
                </TooltipContent>
              </Tooltip>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={navigateToNextDay}
                    variant="outline"
                    size="sm"
                    className="min-h-[44px] min-w-[44px] touch-manipulation"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Next day (swipe left)</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        )}
        
        <div className="bg-white/40 backdrop-blur-sm rounded-xl border border-gray-200/60 overflow-hidden">
          {isMobile ? (
            // Mobile: Single day view with vertical timeline
            <div className="p-4">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {DAYS[selectedDate.getDay()]}
                </h3>
                <p className="text-sm text-gray-600">
                  {format(selectedDate, 'MMMM d, yyyy')}
                </p>
              </div>
              
              <div className="space-y-3">
                {TIME_SLOTS.map(timeSlot => {
                  const dayName = DAYS[selectedDate.getDay()];
                  const schedulesInSlot = schedulesByDayAndTime[dayName]?.[timeSlot] || [];
                  
                  return (
                    <div key={timeSlot} className="flex items-start space-x-3">
                      <div className="w-16 flex-shrink-0 text-center pt-2">
                        <div className="text-sm font-medium text-gray-900">{timeSlot}</div>
                      </div>
                      
                      <div className="flex-1 min-h-[60px]">
                        {schedulesInSlot.length > 0 ? (
                          <div className="space-y-2">
                            {schedulesInSlot.map(schedule => {
                              const classItem = schedule.classData;
                              
                              return (
                                <div 
                                  key={schedule.id} 
                                  className={cn(
                                    "p-3 rounded-lg border text-sm cursor-pointer transition-all hover:shadow-md min-h-[44px] touch-manipulation",
                                    getLevelColor(classItem?.level)
                                  )}
                                >
                                  <div className="font-medium mb-1">
                                    {classItem?.name || 'Unnamed Class'}
                                  </div>
                                  <div className="flex items-center text-xs mb-1">
                                    <Clock className="h-3 w-3 mr-1" />
                                    <span>
                                      {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                                    </span>
                                  </div>
                                  {classItem?.location && (
                                    <div className="text-xs text-gray-600">
                                      {classItem.location}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="h-12 flex items-center justify-center text-gray-400 text-sm">
                            No classes
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            // Desktop: Original 7-day grid view enhanced
            <>
              {/* Calendar Header - Days of Week */}
              <div className="grid grid-cols-7 bg-gray-50">
                {DAYS.map(day => (
                  <div key={day} className="p-3 text-center font-semibold text-gray-700 border-r last:border-r-0">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar Body */}
              <div className="grid grid-cols-7 divide-x divide-gray-200">
                {DAYS.map(day => (
                  <div key={day} className="min-h-[600px] divide-y divide-gray-100">
                    {TIME_SLOTS.map(timeSlot => {
                      const schedulesInSlot = schedulesByDayAndTime[day][timeSlot] || [];
                      
                      return (
                        <div key={`${day}-${timeSlot}`} className="p-2 relative min-h-[80px] hover:bg-gray-50/50 transition-colors">
                          <div className="text-xs text-gray-500 mb-1 font-medium">{timeSlot}</div>
                          
                          {schedulesInSlot.map(schedule => {
                            const classItem = schedule.classData;
                            
                            return (
                              <div 
                                key={schedule.id} 
                                className={cn(
                                  "p-2 rounded-md border text-sm mb-1 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02]",
                                  getLevelColor(classItem?.level)
                                )}
                              >
                                <div className="font-medium truncate">
                                  {classItem?.name || 'Unnamed Class'}
                                </div>
                                <div className="flex items-center text-xs mt-1">
                                  <Clock className="h-3 w-3 mr-1" />
                                  <span>
                                    {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                                  </span>
                                </div>
                                {classItem?.location && (
                                  <div className="text-xs mt-1 truncate opacity-80">
                                    📍 {classItem.location}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        
        {/* Mobile Swipe Hint */}
        {isMobile && (
          <div className="text-center py-2">
            <p className="text-xs text-gray-500">
              ← Swipe to navigate between days →
            </p>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

// Export the component with display name for debugging
CalendarScheduleView.displayName = 'CalendarScheduleView';

export default CalendarScheduleView;
